import React, { Fragment } from 'react';
import { Alert, Box, Button, Card, Heading, Set, styled } from 'bumbag';
import { Form, Formik } from 'formik';
import _get from 'lodash/get';
import Lever from 'react-lever';
import Loads from 'react-loads';
import Toggled from 'react-toggled';

import ErrorMessage from '@medipass/react-core/components/ErrorMessage';
import WithLoadingIndicator from '@medipass/react-core/WithLoadingIndicator';
import sdk from '@medipass/web-sdk';

import MemberDetailsFields from './MemberDetailsFields';

import SubmitCancelButtons from '../../SubmitCancelButtons';
import AddHealthFundAccount from '../health-fund-accounts/AddHealthFundAccount';
import AddMedicareDetails from '../health-fund-accounts/AddMedicareDetails';
import HealthFundCardFields from '../health-fund-accounts/HealthFundCardFields';
import HealthFundDropdown from '../health-fund-accounts/HealthFundDropdown';
const SpacedCenteredRow = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
type Props = {
  initialValues?: Record<string, any> | null | undefined;
  isDependant?: boolean;
  isSelectHealthFundDisabled?: boolean;
  isSelectMedicareDisabled?: boolean;
  onClickCancel: (...args: Array<any>) => any;
  onDeleteHealthFundAccount?: ((...args: Array<any>) => any) | null | undefined;
  onDeleteMedicareAccount?: ((...args: Array<any>) => any) | null | undefined;
  onSubmit: (...args: Array<any>) => any;
  showDeleteHealthFund?: boolean | null | undefined;
  showDeleteMedicareDetails?: boolean | null | undefined;
};
export default class MemberDetailsForm extends React.Component<Props> {
  static defaultProps = {
    initialValues: {
      firstName: '',
      lastName: '',
      dob: '',
      email: '',
      mobile: '',
      healthFundAccount: {
        healthFund: '',
        cardIssueNumber: '',
        cardNumber: '',
        cardRank: '',
        issueDate: '',
        membershipNumber: '',
      },
      medicareAccount: {
        cardRank: '',
        expiryDate: '',
        healthFund: '',
        membershipNumber: '',
      },
    },
    isDependant: false,
    isSelectHealthFundDisabled: false,
    isSelectMedicareDisabled: false,
    onDeleteHealthFundAccount: null,
    onDeleteMedicareAccount: null,
    showDeleteHealthFund: false,
    showDeleteMedicareDetails: false,
  };
  handleDeleteHealthFund = async ({
    setOff,
    setFieldValue,
  }: {
    setOff: (...args: Array<any>) => any;
    setFieldValue: (...args: Array<any>) => any;
  }) => {
    const { isDependant, onDeleteHealthFundAccount } = this.props;

    if (onDeleteHealthFundAccount) {
      await onDeleteHealthFundAccount();
    }

    // If adding a dependant, then we only want to clear the card rank and not hide the fields.
    if (!isDependant) {
      setFieldValue('healthFundAccount.healthFundId', '');
      setFieldValue('healthFundAccount.cardIssueNumber', '');
      setFieldValue('healthFundAccount.cardNumber', '');
      setFieldValue('healthFundAccount.issueDate', '');
      setFieldValue('healthFundAccount.membershipNumber', '');
      setOff();
    }

    setFieldValue('healthFundAccount.cardRank', '');
  };
  handleDeleteMedicareDetails = async ({
    setFieldValue,
    setOff,
  }: {
    setFieldValue: (...args: Array<any>) => any;
    setOff: (...args: Array<any>) => any;
  }) => {
    const { isDependant, onDeleteMedicareAccount } = this.props;

    if (onDeleteMedicareAccount) {
      await onDeleteMedicareAccount();
    }

    // If adding a dependant, then we only want to clear the card rank and not hide the fields.
    if (!isDependant) {
      setFieldValue('medicareAccount.healthFundId', '');
      setFieldValue('medicareAccount.membershipNumber', '');
      setFieldValue('medicareAccount.expiryDate', '');
      setFieldValue('medicareAccount.healthFundAccountId', '');
      setOff();
    }

    setFieldValue('medicareAccount.cardRank', '');
  };
  getMedicareHealthFund = async ({ setFieldValue }: { setFieldValue: (...args: Array<any>) => any }) => {
    const healthFund = await sdk.healthfunds.getMedicareHealthFund();
    setFieldValue('medicareAccount.healthFundId', _get(healthFund, '_id'));
    return healthFund;
  };
  submitMemberDetails = async (data: Record<string, any>) => {
    const { onSubmit } = this.props;
    return await onSubmit(data);
  };
  render = () => {
    const { initialValues, onClickCancel } = this.props;
    return (
      <Loads load={this.submitMemberDetails}>
        {/* @ts-expect-error TS(7031): Binding element 'error' implicitly has an 'any' ty... Remove this comment to see the full error message */}
        {({ error, load: handleSubmitMemberDetails, isLoading, isError }) => (
          <Box>
            {/* @ts-expect-error TS(2322): Type 'Record<string, any> | null | undefined' is n... Remove this comment to see the full error message */}
            <Formik initialValues={initialValues} onSubmit={handleSubmitMemberDetails}>
              {({ errors, setFieldValue, values }) => {
                return (
                  <Form noValidate>
                    <Set display="block" orientation="vertical" spacing="major-2">
                      {this.renderPersonalDetails()}
                      {this.renderHealthFundDetails({
                        // @ts-expect-error TS(2322): Type '(field: string, value: any, shouldValidate?:... Remove this comment to see the full error message
                        setFieldValue,
                        values,
                      })}
                      <Lever feature="medicare">
                        {this.renderMedicareDetails({
                          setFieldValue,
                          values,
                        })}
                      </Lever>
                      <SubmitCancelButtons
                        cancelText="CANCEL"
                        isLoading={isLoading}
                        onClickCancel={onClickCancel}
                        submitText="SAVE"
                      />
                      {isError && (
                        <Alert variant="tint" hasIcon type="danger">
                          <ErrorMessage description="Unable to submit member" err={error} />
                        </Alert>
                      )}
                    </Set>
                  </Form>
                );
              }}
            </Formik>
          </Box>
        )}
      </Loads>
    );
  };
  renderPersonalDetails = () => (
    <Card>
      <h2>Personal details</h2>
      <MemberDetailsFields isEmailOptional isMobileOptional />
    </Card>
  );
  renderHealthFundDetails = ({ setFieldValue, values }: { setFieldValue: string; values: Record<string, any> }) => {
    const { isDependant, isSelectHealthFundDisabled, showDeleteHealthFund } = this.props;
    const healthFundAccount = values.healthFundAccount;
    const hasHealthFund = Boolean(healthFundAccount.healthFundId);
    return (
      <Card>
        <Toggled defaultOn={hasHealthFund}>
          {({ on, setOff, getTogglerProps }) =>
            on ? (
              <Fragment>
                <SpacedCenteredRow className="margin-bottom-10">
                  <Heading use="h5" fontWeight="semibold">
                    Health fund
                  </Heading>
                  {showDeleteHealthFund && (
                    <Loads
                      load={() =>
                        this.handleDeleteHealthFund({
                          setOff,
                          // @ts-expect-error TS(2322): Type 'string' is not assignable to type '(...args:... Remove this comment to see the full error message
                          setFieldValue,
                        })
                      }
                    >
                      {/* @ts-expect-error TS(7031): Binding element 'handleDeleteHealthFund' implicitl... Remove this comment to see the full error message */}
                      {({ load: handleDeleteHealthFund, isLoading }) => (
                        <Button palette="danger" isLoading={isLoading} variant="ghost" onClick={handleDeleteHealthFund}>
                          <i className="fa fa-sm fa-trash-alt margin-right-5" />
                          Delete health fund
                        </Button>
                      )}
                    </Loads>
                  )}
                </SpacedCenteredRow>
                <HealthFundDropdown
                  fieldNamePrefix="healthFundAccount"
                  isDisabled={isSelectHealthFundDisabled || isDependant}
                />
                <HealthFundCardFields
                  cardIssueNumber={healthFundAccount.cardIssueNumber}
                  cardNumber={healthFundAccount.cardNumber}
                  cardholder={`${values.firstName} ${values.lastName}`}
                  fieldNamePrefix="healthFundAccount"
                  healthFundId={_get(values, 'healthFundAccount.healthFundId')}
                  isDependant={isDependant}
                  issueDate={healthFundAccount.issueDate}
                  membershipNumber={healthFundAccount.membershipNumber}
                  cardRank={healthFundAccount.cardRank}
                />
              </Fragment>
            ) : (
              <Fragment>
                <AddHealthFundAccount buttonProps={{ ...getTogglerProps(), disabled: isDependant }} />
                {isSelectHealthFundDisabled && isDependant && (
                  <Alert
                    hasIcon
                    variant="tint"
                    marginTop="major-2"
                    type="info"
                    style={{
                      width: '100%',
                    }}
                  >
                    To add a health fund to a new member, the Tyro Health account holder needs to first add a health
                    fund to their account.
                  </Alert>
                )}
              </Fragment>
            )
          }
        </Toggled>
      </Card>
    );
  };
  renderMedicareDetails = ({
    setFieldValue,
    values,
  }: {
    setFieldValue: (...args: Array<any>) => any;
    values: Record<string, any>;
  }) => {
    const { isDependant, isSelectMedicareDisabled, showDeleteMedicareDetails } = this.props;
    const medicareAccount = values.medicareAccount;
    const hasMedicare = Boolean(medicareAccount.healthFundId);
    return (
      <Card>
        <Toggled defaultOn={hasMedicare}>
          {({ on, getTogglerProps, setOff }) =>
            on ? (
              <Fragment>
                <SpacedCenteredRow className="margin-bottom-10">
                  <Heading use="h5" fontWeight="semibold">
                    Medicare
                  </Heading>
                  {showDeleteMedicareDetails && (
                    <Loads
                      load={() =>
                        this.handleDeleteMedicareDetails({
                          setFieldValue,
                          setOff,
                        })
                      }
                    >
                      {/* @ts-expect-error TS(7031): Binding element 'handleDeleteMedicareDetails' impl... Remove this comment to see the full error message */}
                      {({ load: handleDeleteMedicareDetails, isLoading }) => (
                        <Button
                          palette="danger"
                          isLoading={isLoading}
                          variant="ghost"
                          onClick={handleDeleteMedicareDetails}
                        >
                          <i className="fa fa-sm fa-trash-alt margin-right-5" />
                          Delete Medicare details
                        </Button>
                      )}
                    </Loads>
                  )}
                </SpacedCenteredRow>
                <Loads
                  contextKey="medicareHealthFund"
                  loadOnMount
                  load={() =>
                    this.getMedicareHealthFund({
                      setFieldValue,
                    })
                  }
                >
                  {/* @ts-expect-error TS(7031): Binding element 'medicareHealthFund' implicitly ha... Remove this comment to see the full error message */}
                  {({ response: medicareHealthFund, isLoading, isSuccess }) => (
                    <WithLoadingIndicator isActive={isLoading}>
                      {isSuccess && (
                        <Box data-testid="medicare-details-fields">
                          <HealthFundCardFields
                            cardholder={`${values.firstName} ${values.lastName}`}
                            cardRank={medicareAccount.cardRank}
                            expiryDate={medicareAccount.expiryDate}
                            membershipNumber={medicareAccount.membershipNumber}
                            fieldNamePrefix="medicareAccount"
                            healthFundId={medicareHealthFund._id}
                            isDependant={isDependant}
                          />
                        </Box>
                      )}
                    </WithLoadingIndicator>
                  )}
                </Loads>
              </Fragment>
            ) : (
              <Fragment>
                <AddMedicareDetails buttonProps={{ ...getTogglerProps(), disabled: isDependant }} />
                {isSelectMedicareDisabled && isDependant && (
                  <Alert
                    hasIcon
                    variant="tint"
                    marginTop="major-2"
                    type="info"
                    style={{
                      width: '100%',
                    }}
                  >
                    To add Medicare details for this member, the Tyro Health account holder needs to first add Medicare
                    details to their account.
                  </Alert>
                )}
              </Fragment>
            )
          }
        </Toggled>
      </Card>
    );
  };
}
